import { Controller } from '@hotwired/stimulus';
import {
  disableBackgroundInert,
  enableBackgroundInert,
  lockImageModalPageScroll,
  releaseFocusTrap,
  trapFocus,
  unlockImageModalPageScroll,
} from '../../../utils/FocusTrap';

const MODAL_OPEN_ROOT_CLASS = 'aqui-image--modal-open';

const CLOSE_BUTTON_SELECTOR = '[data-railsui-image-modal-target="close"]';
const CONTROLS_SELECTOR = '[data-railsui-image-modal-target="controls"]';
const PANEL_SELECTOR = '[data-railsui-image-modal-target="panel"]';
const IMAGE_SELECTOR = '[data-railsui-image-modal-target="image"]';
const HIDDEN_CLASS = 'hidden';

export default class extends Controller {
  static targets = ['modal', 'panel', 'image', 'thumbnail', 'expandTrigger'];

  connect() {
    this.handleEscape = this.handleEscape.bind(this);
    this.handleModalClick = this.handleModalClick.bind(this);
    this.inertedElements = [];
    this.trapFocusRafId = null;

    if (this.hasModalTarget) {
      this.modalElement = this.modalTarget;
      this.panelElement = this.hasPanelTarget ? this.panelTarget : null;
      this.imageElement = this.hasImageTarget ? this.imageTarget : null;
      this.portalModal();
      this.modalElement.addEventListener('click', this.handleModalClick);
    }
  }

  openWithKey(event) {
    if (event.key !== 'Enter' && event.key !== ' ' && event.key !== 'Spacebar') return;

    event.preventDefault();
    this.open(event);
  }

  open(event) {
    event.preventDefault();
    event.stopPropagation();

    this.lastTrigger = event.currentTarget;

    if (!this.modalElement) return;

    this.syncModalImage();
    this.modalElement.classList.remove(HIDDEN_CLASS);
    this.modalElement.setAttribute('aria-hidden', 'false');
    this.element.classList.add(MODAL_OPEN_ROOT_CLASS);
    lockImageModalPageScroll();
    this.inertedElements = enableBackgroundInert(this.modalElement);
    document.addEventListener('keydown', this.handleEscape);

    this.cancelPendingTrapFocus();
    this.trapFocusRafId = requestAnimationFrame(() => {
      this.trapFocusRafId = null;
      trapFocus(this.modalElement, { initialFocusSelector: CLOSE_BUTTON_SELECTOR });
    });
  }

  close(event) {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }

    if (!this.modalElement) return;

    this.cancelPendingTrapFocus();
    releaseFocusTrap(this.modalElement, { restoreFocus: false });

    this.modalElement.classList.add(HIDDEN_CLASS);
    this.modalElement.setAttribute('aria-hidden', 'true');
    this.element.classList.remove(MODAL_OPEN_ROOT_CLASS);
    unlockImageModalPageScroll();
    disableBackgroundInert(this.inertedElements);
    this.inertedElements = [];
    document.removeEventListener('keydown', this.handleEscape);

    if (this.lastTrigger && typeof this.lastTrigger.focus === 'function') {
      const focusTarget = this.focusRestoreTarget();
      focusTarget?.focus();
      this.lastTrigger = null;
    }
  }

  cancelPendingTrapFocus() {
    if (this.trapFocusRafId == null) return;

    cancelAnimationFrame(this.trapFocusRafId);
    this.trapFocusRafId = null;
  }

  focusRestoreTarget() {
    if (!this.lastTrigger) return null;

    if (this.lastTrigger.getAttribute('tabindex') === '-1' && this.hasExpandTriggerTarget) {
      return this.expandTriggerTarget;
    }

    return this.lastTrigger;
  }

  handleEscape(event) {
    if (event.key !== 'Escape' || !this.modalElement) return;
    if (this.modalElement.classList.contains(HIDDEN_CLASS)) return;

    event.preventDefault();
    this.close();
  }

  handleModalClick(event) {
    if (event.target.closest(CLOSE_BUTTON_SELECTOR)) {
      this.close(event);
      return;
    }

    if (event.target.closest(CONTROLS_SELECTOR)) return;

    if (!event.target.closest(PANEL_SELECTOR)) {
      this.close(event);
    }
  }

  syncModalImage() {
    const modalImage = this.imageElement || this.modalElement?.querySelector(IMAGE_SELECTOR);
    if (!modalImage) return;

    const thumbnail = this.hasThumbnailTarget
      ? this.thumbnailTarget
      : this.element.querySelector('[data-railsui-image-modal-target="thumbnail"]');
    const thumbnailSrc = thumbnail?.getAttribute('src');
    if (!thumbnailSrc) return;

    modalImage.setAttribute('src', thumbnailSrc);
  }

  disconnect() {
    this.cancelPendingTrapFocus();
    document.removeEventListener('keydown', this.handleEscape);

    if (this.modalElement) {
      this.modalElement.removeEventListener('click', this.handleModalClick);
      this.element.classList.remove(MODAL_OPEN_ROOT_CLASS);
      unlockImageModalPageScroll();
      disableBackgroundInert(this.inertedElements);
      this.inertedElements = [];

      releaseFocusTrap(this.modalElement, { restoreFocus: false });
      this.restoreModal();
    }
  }

  portalModal() {
    if (!this.modalElement || this.modalElement.dataset.portaled === 'true') return;

    this.modalHome = document.createComment('aqui-image-modal-home');
    this.modalElement.before(this.modalHome);
    document.body.appendChild(this.modalElement);
    this.modalElement.dataset.portaled = 'true';
  }

  restoreModal() {
    if (!this.modalElement || !this.modalHome) return;

    this.modalHome.replaceWith(this.modalElement);
    delete this.modalElement.dataset.portaled;
    this.modalHome = null;
  }
}
